import { useEffect, useMemo, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  ensureLocalPairForSetup,
  addRemotePairFromWords,
  getStoredPairs,
  parseAndValidatePairingWords,
  removePair,
} from '../utils/pairing';
import { useIdentity } from '../hooks/useIdentity';
import { getStoredRelays } from '../utils/relay';
import { runDevice1Confirmation } from '../services/pairingConfirm';
import { WordInputGrid } from './WordInputGrid';

const PAIRING_DISPLAY_PATH = '/pairing-display';
const QR_SIZE = 256;

function buildPairingDisplayUrl(): string {
  return `${window.location.origin}${PAIRING_DISPLAY_PATH}`;
}

export interface PairingSetupBlockProps {
  onDone?: () => void;
  isAddingDevice?: boolean;
}

export function PairingSetupBlock({
  onDone,
  isAddingDevice = false,
}: PairingSetupBlockProps): JSX.Element {
  const navigate = useNavigate();
  const { identity, createNewIdentity } = useIdentity();
  const [words, setWords] = useState<string[]>([]);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [remoteWordsInput, setRemoteWordsInput] = useState<string[]>([]);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const [hasCopiedToSecondDevice, setHasCopiedToSecondDevice] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [visibleWordIndices, setVisibleWordIndices] = useState<Set<number>>(new Set());

  const toggleWordVisibility = (index: number): void => {
    setVisibleWordIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleGenerateAnotherKey = (): void => {
    const pairs = getStoredPairs();
    const local = pairs.find((p) => p.is_local);
    if (local !== undefined) {
      removePair(local.uuid);
    }
    createNewIdentity();
  };

  useEffect(() => {
    if (identity !== null && identity.publicKey !== undefined) {
      const w = ensureLocalPairForSetup(identity.publicKey);
      setWords(w);
    }
  }, [identity]);

  const url = useMemo(() => buildPairingDisplayUrl(), []);

  useEffect(() => {
    if (words.length === 0) {
      return;
    }
    QRCode.toDataURL(url, { width: QR_SIZE })
      .then(setQrDataUrl)
      .catch((err: unknown) => {
        console.error('QR generation failed:', err);
      });
  }, [words, url]);

  const handleSubmitRemote = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setRemoteError(null);
    const wordsText = remoteWordsInput.join(' ');
    const parsed = parseAndValidatePairingWords(wordsText);
    if (parsed === null) {
      setRemoteError('Mots invalides. 17 mots requis.');
      return;
    }
    const newPair = addRemotePairFromWords(parsed, []);
    if (newPair === null) {
      setRemoteError('Mots invalides. Vérifiez la saisie.');
      return;
    }
    const pairs = getStoredPairs();
    const local = pairs.find((p) => p.is_local);
    if (identity === null || local === undefined || identity.privateKey === undefined) {
      setRemoteWordsInput([]);
      if (onDone !== undefined) {
        onDone();
      } else {
        navigate('/manage-pairs');
      }
      return;
    }
    const relays = getStoredRelays().filter((r) => r.enabled);
    if (relays.length === 0) {
      setRemoteError('Aucun relais activé. Configurez les relais pour finaliser le pairing.');
      return;
    }
    const remotePublicKey = newPair.publicKey;
    setIsConfirming(true);
    try {
      await runDevice1Confirmation({
        pairLocal: local.uuid,
        pairRemote: newPair.uuid,
        identity,
        relays,
        remotePublicKey,
      });
    } catch (err) {
      console.error('Pairing confirmation (device 1):', err);
      setRemoteError(
        err instanceof Error ? err.message : 'Erreur lors de la confirmation du pairing.',
      );
      setIsConfirming(false);
      return;
    }
    setIsConfirming(false);
    setRemoteWordsInput([]);
    if (onDone !== undefined) {
      onDone();
    } else {
      navigate('/manage-pairs');
    }
  };

  const setupHeading = isAddingDevice
    ? 'Ajouter un appareil'
    : 'Configurer le pairing avec un 2ᵉ appareil';

  return (
    <div role="region" aria-labelledby="pairing-setup-block-heading">
      <h3 id="pairing-setup-block-heading">{setupHeading}</h3>
      {words.length > 0 && (
        <>
          {!hasCopiedToSecondDevice ? (
            <>
              <p>
                <strong>Mots du 1ᵉʳ appareil</strong> — à saisir sur le 2ᵉ (QR) :
              </p>
              <p style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  onClick={handleGenerateAnotherKey}
                  aria-label="Générer une autre clé publique"
                >
                  Générer une autre clé publique
                </button>
              </p>
              <div
                aria-label="Mots 1er appareil"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  fontFamily: 'monospace',
                  fontSize: '1rem',
                  marginTop: '0.5rem',
                }}
              >
                {words.map((word, index) => {
                  const visible = visibleWordIndices.has(index);
                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: 'var(--color-info-bg, #e8f4fd)',
                        borderRadius: '4px',
                        border: '1px solid var(--color-info-border, #93c5fd)',
                      }}
                    >
                      <span style={{ fontWeight: 'bold' }}>{index + 1}.</span>
                      <span style={{ minWidth: '80px' }}>
                        {visible ? word : '•••••'}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleWordVisibility(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.125rem',
                          fontSize: '0.875rem',
                          color: 'var(--color-text-secondary, #666)',
                        }}
                        aria-label={`${visible ? 'Masquer' : 'Afficher'} le mot ${index + 1}`}
                        title={`${visible ? 'Masquer' : 'Afficher'} le mot ${index + 1}`}
                      >
                        {visible ? '👁' : '👁‍🗨'}
                      </button>
                    </div>
                  );
                })}
              </div>
              {qrDataUrl !== null && (
                <p>
                  <img
                    src={qrDataUrl}
                    alt="QR code : URL pour ouvrir la page de pairing sur le 2e appareil"
                    width={QR_SIZE}
                    height={QR_SIZE}
                  />
                </p>
              )}
              <p>
                <strong>URL pour le 2ᵉ appareil :</strong>{' '}
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </a>
              </p>
              <p>
                <button
                  type="button"
                  onClick={() => setHasCopiedToSecondDevice(true)}
                >
                  {isAddingDevice
                    ? "J'ai copié ces mots sur le nouvel appareil"
                    : "J'ai copié ces mots sur mon deuxième device"}
                </button>
              </p>
            </>
          ) : (
            <section aria-labelledby="remote-words-heading">
              <h4 id="remote-words-heading">
                {isAddingDevice ? 'Mots du nouvel appareil' : 'Mots du 2ᵉ appareil'}
              </h4>
              <form
                onSubmit={(ev) => void handleSubmitRemote(ev)}
                aria-label="Saisir les mots du 2e appareil"
              >
                <label htmlFor="remote-pairing-words">
                  Mots affichés par le 2ᵉ appareil
                  <WordInputGrid
                    id="remote-pairing-words"
                    value={remoteWordsInput}
                    onChange={setRemoteWordsInput}
                    aria-describedby={remoteError !== null ? 'remote-words-err' : undefined}
                    aria-label="Saisir les 17 mots du 2e appareil"
                    wordCount={17}
                  />
                </label>
                {remoteError !== null && (
                  <p id="remote-words-err" role="alert" style={{ color: 'var(--color-error)' }}>
                    {remoteError}
                  </p>
                )}
                <button type="submit" disabled={isConfirming}>
                  {isConfirming ? 'Finalisation…' : 'Associer le pair'}
                </button>
              </form>
            </section>
          )}
        </>
      )}
    </div>
  );
}
